import { Text } from '@byecode/ui'
import { VariableADTvalue, VariableType } from '@lighthouse/core'
import {
    type FlowNode,
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    type SendEmailActionConfig,
    useAtomData
} from '@lighthouse/shared'
import { safeToString } from '@lighthouse/tools'
import { useNodesData } from '@xyflow/react'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import * as CM from '../styles'
import { textRichVariableName } from '../util'
import * as SC from './styles'

type EmailCardProps = {
    config: SendEmailActionConfig
}

const variableTypeName = (type: Omit<VariableType, 'COMMITTER'>) => {
    if (type === 'UPSTREAM') {
        return 'upstreamVariable'
    } else if (type === 'ARG_VARIABLE') {
        return 'argVariable'
    }
}

const EmailCard: React.FC<EmailCardProps> = ({ config }) => {
    const { recipients, cc, senderAccountType } = config
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const nodeIds = useMemo(() => {
        // return recipients.map(item => item.upstreamVariable?.nodeId)
        const nodeIds: string[] = []
        recipients.forEach(item => {
            if (item.type === VariableType.ARG_VARIABLE) {
                nodeIds.push(item.argVariable?.nodeId ?? '')
            }
            if (item.type === VariableType.UPSTREAM) {
                nodeIds.push(item.upstreamVariable?.nodeId ?? '')
            }
        })
        return nodeIds
    }, [recipients])

    const nodes = useNodesData(nodeIds) as FlowNode[]

    const actorUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: getAllCanBeUpstreamNodes(nodes),
                allUpstreamNodes: nodes
            }),
        [workflowList, dataSourceList, nodes]
    )

    const textList = useMemo(() => {
        const arr: string[] = []
        recipients.forEach(item => {
            if (item.type === VariableType.VALUE) {
                arr.push(safeToString(item?.valueVariable?.value) || '')
            } else {
                const val = textRichVariableName({
                    // userOption: userOptionAddressee,
                    upstreamOptions: actorUpstreamOptions,
                    // pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                    data: {
                        ...item,
                        [variableTypeName(item.type) as VariableType]: 'ref' in item ? item?.ref : undefined
                    } as VariableADTvalue
                })
                if (val) {
                    arr.push(val)
                }
            }
        })
        return arr
    }, [actorUpstreamOptions, recipients])

    const otherPlatform = useMemo(() => {
        return otherPlatformList?.find(item => item.type === senderAccountType) ?? []
    }, [otherPlatformList, senderAccountType])

    if (!textList.length || !otherPlatform) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <SC.TypeName>收件人</SC.TypeName>
            <CM.FieldList>
                {textList.map(text => (
                    <CM.FieldItem key={text}>
                        <CM.FieldItemTitle>
                            {text.startsWith('{') && text.endsWith('}') ? (
                                <Text size={12} color="var(--color-purple-500)">
                                    {text}
                                </Text>
                            ) : (
                                <Text size={12}>{text}</Text>
                            )}
                        </CM.FieldItemTitle>
                    </CM.FieldItem>
                ))}
            </CM.FieldList>
        </CM.CardItemContainer>
    )
}

export default EmailCard
